import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";
import { DEFAULT_CHIPS, GREETING, INTEREST_URL, matchTopic } from "./askKB";

const INITIAL = [{ from: "bot", text: GREETING }];

export default function AskChaplain() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState(INITIAL);
  const [chips, setChips] = useState(DEFAULT_CHIPS);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const lenis = window.__lenis;
    if (lenis) lenis.stop();
    const html = document.documentElement;
    const prev = html.style.overflow;
    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(timerRef.current);
      if (lenis) lenis.start();
      html.style.overflow = prev;
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs, typing, open]);

  // Closing clears the conversation from memory entirely — nothing is stored.
  const close = () => {
    clearTimeout(timerRef.current);
    setOpen(false);
    setTyping(false);
    setMsgs(INITIAL);
    setChips(DEFAULT_CHIPS);
  };

  const ask = (q) => {
    const text = String(q).trim().slice(0, 240);
    if (!text || typing) return;
    setMsgs((m) => [...m, { from: "user", text }]);
    setInput("");
    setTyping(true);
    timerRef.current = setTimeout(() => {
      const { answer, chips: nextChips } = matchTopic(text);
      setMsgs((m) => [...m, { from: "bot", text: answer }]);
      setChips(nextChips);
      setTyping(false);
    }, 450);
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="ask-chat-toggle"
          aria-label="Open Ask Chaplain at Sea helper"
          className="fixed bottom-4 right-4 z-50 inline-flex items-center gap-2.5 border border-gold/50 bg-navy px-4 py-3 text-cream shadow-[0_10px_30px_rgba(11,25,44,0.45)] transition-colors duration-300 hover:border-gold hover:bg-navy-abyss"
        >
          <MessageCircle size={17} strokeWidth={1.5} className="text-gold-light" aria-hidden="true" />
          <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Ask Chaplain at Sea</span>
        </button>
      )}

      {open && (
        <div
          role="dialog"
          aria-label="Ask Chaplain at Sea"
          data-testid="ask-chat-panel"
          data-lenis-prevent
          className="fixed bottom-4 left-4 right-4 z-50 flex h-[min(72vh,560px)] flex-col border border-gold/40 bg-navy text-cream shadow-[0_24px_60px_rgba(11,25,44,0.55)] sm:left-auto sm:w-[380px]"
        >
          <div className="flex items-start justify-between gap-3 border-b border-gold/20 bg-navy-abyss/60 px-4 py-3.5">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-gold-light">Guided FAQ helper</p>
              <p className="mt-1 font-serif text-lg leading-tight">Ask Chaplain at Sea</p>
            </div>
            <button
              type="button"
              onClick={close}
              data-testid="ask-close-btn"
              aria-label="Close helper"
              className="border border-gold/30 p-1.5 text-cream/70 transition-colors duration-300 hover:border-gold hover:text-gold-light"
            >
              <X size={15} strokeWidth={1.5} aria-hidden="true" />
            </button>
          </div>
          <p className="border-b border-gold/10 px-4 py-2 text-[11px] leading-snug text-cream/55" data-testid="ask-privacy-note">
            Nothing you type is sent or saved. This chat can&rsquo;t take bookings or payments.
          </p>

          <div
            ref={scrollRef}
            data-lenis-prevent
            className="flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-4"
            data-testid="ask-message-list"
          >
            {msgs.map((m, i) => (
              <div
                key={i}
                data-testid={"ask-message-" + i}
                className={
                  "max-w-[88%] px-3.5 py-2.5 text-sm leading-relaxed " +
                  (m.from === "user"
                    ? "ml-auto bg-gold text-navy"
                    : "border border-gold/20 bg-navy-abyss/70 text-cream/90")
                }
              >
                {m.text}
              </div>
            ))}
            {typing && (
              <div className="max-w-[88%] border border-gold/20 bg-navy-abyss/70 px-3.5 py-2.5 text-sm text-cream/60" data-testid="ask-typing">
                &hellip;
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 px-4 pb-2.5" data-testid="ask-chips">
            {chips.map((c, i) => (
              <button
                key={c}
                type="button"
                onClick={() => ask(c)}
                data-testid={"ask-chip-" + i}
                className="border border-gold/35 px-2.5 py-1.5 text-[11px] leading-snug text-cream/80 transition-colors duration-300 hover:border-gold hover:bg-gold hover:text-navy"
              >
                {c}
              </button>
            ))}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              ask(input);
            }}
            className="flex items-center gap-2 border-t border-gold/20 p-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              maxLength={240}
              placeholder="Ask a question — no details needed"
              data-testid="ask-chat-input"
              aria-label="Ask a question"
              className="field-input min-w-0 flex-1 bg-navy-abyss/60 text-sm"
            />
            <button
              type="submit"
              data-testid="ask-send-btn"
              aria-label="Send question"
              className="shrink-0 bg-gold p-2.5 text-navy transition-colors duration-300 hover:bg-gold-light"
            >
              <Send size={15} strokeWidth={1.5} aria-hidden="true" />
            </button>
          </form>

          <div className="flex items-center justify-between gap-3 border-t border-gold/20 px-4 py-2.5">
            <span className="text-[11px] text-cream/55">Unconfirmed details are to be announced.</span>
            <a
              href={INTEREST_URL}
              data-testid="ask-interest-link"
              className="text-[11px] font-medium uppercase tracking-[0.14em] text-gold-light underline underline-offset-4 transition-colors duration-300 hover:text-gold"
            >
              Open the interest form
            </a>
          </div>
        </div>
      )}
    </>
  );
}
